import { useEffect, useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { sk } from 'date-fns/locale';
import { fetchUserActivityLogs, fetchActivityLogUsers } from '../../store/actions/userActivityLog';

const formatDate = (value) => new Date(value).toLocaleString('sk-SK', { dateStyle: 'medium', timeStyle: 'short' });

const MODULE_LABELS = {
    ppwr: 'PPWR',
    operationalLog: 'Prevádzkový denník',
    consent: 'Súhlasy',
    warehouse: 'Sklady',
    container: 'Nádoby',
    evidenceSheet: 'Evidenčné listy',
    announcement: 'Ohlásenie odpadov',
    profile: 'Môj profil',
    settings: 'Nastavenia',
    billing: 'Predplatné',
    branch: 'Firma — prevádzky',
    client: 'Firma — odberatelia a dodávatelia',
    auth: 'Prihlásenie a odhlásenie',
};

const ACTION_LABELS = {
    // PPWR
    UNIT_CREATED: 'Vytvorenie jednotky',
    UNIT_UPDATED: 'Úprava jednotky',
    UNIT_ARCHIVED: 'Archivácia jednotky',
    UNIT_UNARCHIVED: 'Obnovenie z archívu',
    COMPONENT_ADDED: 'Pridanie komponentu',
    COMPONENT_UPDATED: 'Úprava komponentu',
    COMPONENT_DELETED: 'Odstránenie komponentu',
    STANDARDS_SAVED: 'Uloženie noriem a špecifikácií',
    DRAWING_DOC_SET: 'Priradenie výkresovej dokumentácie',
    TECH_DOC_GENERATED: 'Generovanie technickej dokumentácie',
    TECH_DOC_DELETED: 'Vymazanie technickej dokumentácie',
    EU_DECLARATION_GENERATED: 'Generovanie EÚ vyhlásenia',
    EU_DECLARATION_DELETED: 'Vymazanie EÚ vyhlásenia',
    DOC_UPLOADED: 'Nahranie dokladu',
    DOC_DELETED: 'Vymazanie dokladu',
    // Prevádzkový denník
    LOG_CREATED: 'Vytvorenie denníka',
    LOG_UPDATED: 'Úprava denníka',
    LOG_DELETED: 'Vymazanie denníka',
    ENTRY_CREATED: 'Vytvorenie denného záznamu',
    ENTRY_UPDATED: 'Úprava denného záznamu',
    ENTRY_DELETED: 'Vymazanie denného záznamu',
    // Súhlasy
    CONSENT_CREATED: 'Vytvorenie súhlasu',
    CONSENT_UPDATED: 'Úprava súhlasu',
    CONSENT_DELETED: 'Vymazanie súhlasu',
    // Sklady
    WAREHOUSE_CREATED: 'Vytvorenie skladu',
    WAREHOUSE_UPDATED: 'Úprava skladu',
    WAREHOUSE_DELETED: 'Vymazanie skladu',
    // Nádoby
    CONTAINER_CREATED: 'Vytvorenie nádoby',
    CONTAINER_UPDATED: 'Úprava nádoby',
    CONTAINER_DELETED: 'Vymazanie nádoby',
    // Evidenčné listy
    SHEET_CREATED: 'Vytvorenie evidenčného listu',
    SHEET_UPDATED: 'Úprava evidenčného listu',
    SHEET_DELETED: 'Vymazanie evidenčného listu',
    WASTE_CREATED: 'Pridanie príjmu/výdaja',
    WASTE_UPDATED: 'Úprava príjmu/výdaja',
    WASTE_DELETED: 'Vymazanie príjmu/výdaja',
    LAST_YEAR_BALANCE_CREATED: 'Zadanie počiatočného stavu',
    PDF_GENERATED: 'Generovanie PDF',
    // Ohlásenie odpadov
    XML_GENERATED: 'Generovanie XML',
    // Účet — profil
    PROFILE_UPDATED: 'Úprava profilu',
    PASSWORD_CHANGED: 'Zmena hesla',
    EMAIL_CHANGED: 'Zmena emailu',
    USERNAME_CHANGED: 'Zmena prihlasovacieho mena',
    // Účet — nastavenia
    TENANT_UPDATED: 'Úprava údajov subjektu',
    MODULE_ACTIVATED: 'Aktivácia modulu',
    MEMBER_ADDED: 'Pridanie člena',
    MEMBER_REMOVED: 'Odobratie člena',
    MEMBER_MODULES_UPDATED: 'Úprava prístupov člena',
    SIGNATURE_UPLOADED: 'Nahranie podpisu',
    SIGNATURE_DELETED: 'Vymazanie podpisu',
    // Účet — predplatné
    SUBSCRIPTION_ACTIVATED: 'Aktivácia predplatného',
    // Firma — prevádzky
    BRANCH_CREATED: 'Vytvorenie prevádzky',
    BRANCH_UPDATED: 'Úprava prevádzky',
    BRANCH_DELETED: 'Vymazanie prevádzky',
    // Firma — odberatelia a dodávatelia
    CLIENT_CREATED: 'Vytvorenie odberateľa/dodávateľa',
    CLIENT_UPDATED: 'Úprava odberateľa/dodávateľa',
    CLIENT_DELETED: 'Vymazanie odberateľa/dodávateľa',
    CLIENT_BRANCH_ADDED: 'Pridanie prevádzky odberateľa/dodávateľa',
    CLIENT_BRANCH_UPDATED: 'Úprava prevádzky odberateľa/dodávateľa',
    CLIENT_BRANCH_DELETED: 'Vymazanie prevádzky odberateľa/dodávateľa',
    // Prihlásenie a odhlásenie
    LOGIN: 'Prihlásenie',
    LOGOUT: 'Odhlásenie',
};

const PAGE_SIZE = 50;

const getPageRange = (current, total) => {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    if (current <= 4) return [1, 2, 3, 4, 5, '…', total];
    if (current >= total - 3) return [1, '…', total - 4, total - 3, total - 2, total - 1, total];
    return [1, '…', current - 1, current, current + 1, '…', total];
};

const thStyle = { padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#9AA0B4', textAlign: 'left', whiteSpace: 'nowrap' };
const tdStyle = { padding: '10px 12px', verticalAlign: 'middle', fontSize: 13, color: '#0B1157' };

const pageBtnStyle = (active, disabled) => ({
    border: 'none', borderRadius: 8, padding: '4px 10px',
    fontSize: 13, fontWeight: 600,
    cursor: disabled ? 'default' : 'pointer',
    minWidth: 32,
    backgroundColor: active ? '#0B1157' : '#F1F2F6',
    color: active ? '#4DFFFF' : disabled ? '#C8CBDA' : '#5B6178',
});

const inputStyle = {
    fontSize: 13, padding: '6px 10px', borderRadius: 8,
    border: '1px solid #D8DAEA', color: '#0B1157', background: '#fff',
    outline: 'none',
};

const ActivityLog = () => {
    const dispatch = useDispatch();
    const [logs, setLogs] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [users, setUsers] = useState([]);

    const [module, setModule] = useState('');
    const [dateFrom, setDateFrom] = useState(null);
    const [dateTo, setDateTo] = useState(null);
    const [userName, setUserName] = useState('');

    useEffect(() => {
        dispatch(fetchActivityLogUsers({ module: module || undefined }))
            .unwrap()
            .then(data => setUsers(data))
            .catch(() => {});
    }, [dispatch, module]);

    const load = useCallback((p = 1) => {
        setLoading(true);
        dispatch(fetchUserActivityLogs({
            module: module || undefined,
            page: p,
            limit: PAGE_SIZE,
            userName: userName || undefined,
            dateFrom: dateFrom ? dateFrom.toISOString() : undefined,
            dateTo: dateTo ? dateTo.toISOString() : undefined,
        }))
            .unwrap()
            .then(data => {
                setLogs(data.logs);
                setTotal(data.total);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [dispatch, module, userName, dateFrom, dateTo]);

    useEffect(() => {
        load(page);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [load, page]);

    const handleFilter = () => {
        setPage(1);
        load(1);
    };

    const handleModuleChange = (value) => {
        setModule(value);
        setUserName('');
        setPage(1);
    };

    const handleClear = () => {
        setModule('');
        setDateFrom(null);
        setDateTo(null);
        setUserName('');
    };

    const hasActiveFilters = !!(module || dateFrom || dateTo || userName);
    const totalPages = Math.ceil(total / PAGE_SIZE) || 1;

    return (
        <div>
            {/* Filtre */}
            <div className="d-flex align-items-end gap-2 mb-3 flex-wrap">
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA0B4', marginBottom: 4 }}>MODUL</div>
                    <select
                        value={module}
                        onChange={e => handleModuleChange(e.target.value)}
                        style={{ ...inputStyle, minWidth: 200 }}
                    >
                        <option value="">Všetky moduly</option>
                        {Object.entries(MODULE_LABELS).map(([key, label]) => (
                            <option key={key} value={key}>{label}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA0B4', marginBottom: 4 }}>OD</div>
                    <DatePicker
                        selected={dateFrom}
                        onChange={date => setDateFrom(date)}
                        selectsStart
                        startDate={dateFrom}
                        endDate={dateTo}
                        dateFormat="dd.MM.yyyy"
                        locale={sk}
                        placeholderText="dd.mm.rrrr"
                        isClearable
                        customInput={<input style={inputStyle} />}
                    />
                </div>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA0B4', marginBottom: 4 }}>DO</div>
                    <DatePicker
                        selected={dateTo}
                        onChange={date => setDateTo(date)}
                        selectsEnd
                        startDate={dateFrom}
                        endDate={dateTo}
                        minDate={dateFrom}
                        dateFormat="dd.MM.yyyy"
                        locale={sk}
                        placeholderText="dd.mm.rrrr"
                        isClearable
                        customInput={<input style={inputStyle} />}
                    />
                </div>
                <div>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9AA0B4', marginBottom: 4 }}>POUŽÍVATEĽ</div>
                    <select
                        value={userName}
                        onChange={e => setUserName(e.target.value)}
                        style={{ ...inputStyle, minWidth: 180 }}
                    >
                        <option value="">Všetci</option>
                        {users.map(u => (
                            <option key={u} value={u}>{u}</option>
                        ))}
                    </select>
                </div>
                <button
                    onClick={handleFilter}
                    style={{
                        ...inputStyle, border: 'none', cursor: 'pointer',
                        backgroundColor: '#0B1157', color: '#fff', fontWeight: 600,
                    }}
                >
                    Filtrovať
                </button>
                {hasActiveFilters && (
                    <button
                        onClick={handleClear}
                        style={{ ...inputStyle, cursor: 'pointer', color: '#9AA0B4', background: 'none', border: 'none' }}
                    >
                        Zrušiť filter
                    </button>
                )}
            </div>

            {loading ? (
                <div style={{ padding: '40px 0', textAlign: 'center', color: '#9AA0B4', fontSize: 13 }}>Načítavam...</div>
            ) : (
                <div style={{ borderRadius: 14, overflowX: 'auto', border: '1px solid #EEF0F8' }}>
                    <table style={{ width: '100%', minWidth: 700, borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: '#F4F5FE' }}>
                                <th style={thStyle}>Dátum</th>
                                <th style={thStyle}>Používateľ</th>
                                <th style={thStyle}>Modul</th>
                                <th style={thStyle}>Akcia</th>
                                <th style={thStyle}>Entita</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 ? (
                                <tr>
                                    <td colSpan={5} style={{ padding: '32px 12px', textAlign: 'center', color: '#9AA0B4', fontSize: 13 }}>
                                        Žiadna aktivita
                                    </td>
                                </tr>
                            ) : logs.map((log, i) => (
                                <tr
                                    key={log._id}
                                    style={{
                                        background: i % 2 === 0 ? '#fff' : '#F9FAFF',
                                        borderTop: '1px solid #EEF0F8',
                                    }}
                                >
                                    <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{formatDate(log.createdAt)}</td>
                                    <td style={tdStyle}>{log.userName || '—'}</td>
                                    <td style={{ ...tdStyle, color: '#5B6178' }}>{MODULE_LABELS[log.module] ?? log.module}</td>
                                    <td style={tdStyle}>{ACTION_LABELS[log.action] ?? log.action}</td>
                                    <td style={{ ...tdStyle, color: '#5B6178' }}>{log.entityName || '—'}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center gap-1 mt-3">
                    <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={pageBtnStyle(false, page === 1)}>‹</button>
                    {getPageRange(page, totalPages).map((p, i) =>
                        p === '…'
                            ? <span key={`ellipsis-${i}`} style={{ padding: '4px 6px', color: '#9AA0B4', fontSize: 13 }}>…</span>
                            : <button key={p} onClick={() => setPage(p)} style={pageBtnStyle(page === p, false)}>{p}</button>
                    )}
                    <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} style={pageBtnStyle(false, page === totalPages)}>›</button>
                </div>
            )}

            <div style={{ marginTop: 12, fontSize: 12, color: '#9AA0B4', textAlign: 'right' }}>
                Celkom: {total} záznamov
            </div>
        </div>
    );
};

export default ActivityLog;
